import { html, type TemplateResult } from 'lit-html';
import { app } from './app.js';

/** Shapes returned by /admin/reports/* (see src/reporting/metrics.ts). */
export interface AgentMetrics {
  agent_id: string;
  agent_name: string | null;
  wrapups: number;
  timeouts: number;
  timeouts_prev: number;
  timeout_rate: number | null;
  avg_acw_seconds: number | null;
  avg_own_wrapup_seconds: number | null;
  close_calls: number;
  close_call_rate: number | null;
  timeout_acw_seconds: number;
  total_acw_seconds: number;
  deactivations: number;
  avg_inactive_seconds: number | null;
  last_timeout_at: string | null;
}

export interface TeamMetrics extends Omit<AgentMetrics, 'agent_id' | 'agent_name' | 'avg_inactive_seconds' | 'last_timeout_at'> {
  agents: number;
  agents_with_timeouts: number;
  acw_limit_seconds: number;
  close_call_seconds: number;
  data_since: string | null;
  prev_covered: boolean;
}

export interface QueueMetrics {
  queue_id: string;
  queue_name: string | null;
  wrapups: number;
  timeouts: number;
  timeouts_prev: number;
  agents_with_timeouts: number;
  timeout_rate: number | null;
  avg_acw_seconds: number | null;
  avg_own_wrapup_seconds: number | null;
  close_calls: number;
  close_call_rate: number | null;
  acw_limit_seconds: number | null;
  deactivations: number;
  avg_inactive_seconds: number | null;
  max_inactive_seconds: number | null;
}

export interface HourMetrics {
  hour: number;
  wrapups: number;
  timeouts: number;
  timeout_rate: number | null;
}

export const DURATION_BUCKETS: [string, string][] = [
  ['under_15s', 'Under 15 s'],
  ['15_30s', '15–30 s'],
  ['30_60s', '30–60 s'],
  ['60_90s', '60–90 s'],
  ['over_90s', '90 s or more'],
  ['timed_out', 'Timed out'],
];

/** The reporting window, shared by the Reports and agent pages. */
export const period = { days: 7 };
const PERIODS: [number, string][] = [
  [1, 'Last 24 hours'],
  [7, 'Last 7 days'],
  [30, 'Last 30 days'],
  [90, 'Last 90 days'],
];

export const periodName = () => (period.days === 1 ? '24 hours' : `${period.days} days`);

export function periodSelect(reload: () => Promise<void>): TemplateResult {
  return html`<select class="compact" aria-label="Period" @change=${async (e: Event) => {
    period.days = Number((e.target as HTMLSelectElement).value);
    await reload();
    app.rerender();
  }}>${PERIODS.map(([n, label]) => html`<option value=${n} ?selected=${n === period.days}>${label}</option>`)}</select>`;
}

/** Change against the previous window of the same length; more timeouts reads as bad. */
export function delta(now: number, prev: number, covered: boolean): TemplateResult {
  if (!covered) return html`<span class="muted" title="Not enough history yet to compare with the previous ${periodName()}">—</span>`;
  const d = now - prev;
  const title = `Previous ${periodName()}: ${prev.toLocaleString()}`;
  if (d === 0) return html`<span class="delta flat" title=${title}>same</span>`;
  return html`<span class="delta ${d > 0 ? 'up' : 'down'}" title=${title}>${d > 0 ? '▲' : '▼'} ${Math.abs(d).toLocaleString()}</span>`;
}

export const num = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString());
